<template>
    <div v-if="ui.flashcardsPanel.opened" class="flex flex-1 flex-col">
        <FlashcardStudyMode v-if="flashcardStudyMode.active" />

        <FlashcardForm v-else-if="isFormVisible" :edit-card="editingCard" @saved="closeForm" @cancel="closeForm" />

        <template v-else>
            <ExportNotesOrHighlight
                :highlights="filteredCards"
                :checked-colors="checkedColors"
                disable-export
                @checked-colors="updateCheckedColors"
            >
                <template #actions>
                    <button
                        type="button"
                        class="inline-flex items-center gap-1.5 rounded-full border border-theme-accent px-3 py-1.5 text-xs font-medium text-theme-accent transition-colors hover:bg-theme-accent hover:text-theme-accent-on focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent focus-visible:ring-offset-2"
                        :aria-label="localizations[lang].flashcard.create"
                        @click="openCreateForm"
                    >
                        <svg-icon icon="flashcard-add-24px" width="14px" height="14px" />
                        <span>{{ localizations[lang].flashcard.create }}</span>
                    </button>
                </template>

                <template #actions-end>
                    <PillIconButton
                        v-tooltip.top="localizations[lang].flashcard.studyAll"
                        icon="rectangle-stack-24px"
                        variant="accent"
                        :disabled="flashcards.length === 0"
                        :aria-label="localizations[lang].flashcard.studyAll"
                        @click="startStudyMode()"
                    />
                </template>
            </ExportNotesOrHighlight>

            <div v-if="flashcards.length === 0" class="py-8 text-center text-sm text-theme-toolbar-icon opacity-60">
                <p>{{ localizations[lang].flashcard.noFlashcards }}</p>
            </div>

            <template v-else>
                <div
                    v-if="filteredCards.length === 0"
                    class="py-8 text-center text-sm text-theme-toolbar-icon opacity-60"
                >
                    <p>{{ localizations[lang].flashcard.noFilterFlashcards }}</p>
                </div>

                <template v-else>
                    <p class="mb-2 text-xs text-theme-toolbar-icon">
                        {{ filteredCards.length }} {{ localizations[lang].flashcards }}
                    </p>
                    <FlashcardList :cards="filteredCards" @edit="openEditForm" @delete="handleDelete" />
                </template>
            </template>
        </template>
    </div>
</template>

<script>
'use strict';

import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import FlashcardForm from './FlashcardForm.vue';
import FlashcardList from './FlashcardList.vue';
import FlashcardStudyMode from './FlashcardStudyMode.vue';
import ExportNotesOrHighlight from '../Notebook/partials/ExportNotesOrHighlight.vue';
import PillIconButton from '../Components/PillIconButton.vue';
import checkedColorsMixin from '../../js/mixins/checkedColorsMixin';

export default {
    name: 'ReaderFlashcards',
    components: {
        FlashcardForm,
        FlashcardList,
        FlashcardStudyMode,
        ExportNotesOrHighlight,
        PillIconButton,
    },
    mixins: [checkedColorsMixin],
    computed: {
        ...mapState([
            'ui',
            'localizations',
            'lang',
            'flashcards',
            'flashcardStudyMode',
            'sharedColors',
            'flashcardFocusUuid',
        ]),
        ...mapGetters(['allHighlighters']),
        isFormVisible() {
            return Boolean(this.ui.flashcardsPanel.form);
        },
        editingCard() {
            return this.ui.flashcardsPanel.form?.card ?? null;
        },
        /**
         * @returns {Array} Flashcards filtered by the selected color slugs
         */
        filteredCards() {
            const checkedSlugs = new Set(this.checkedColors);
            return this.flashcards.filter(card => checkedSlugs.has(card.colorSlug));
        },
    },
    watch: {
        'ui.flashcardsPanel.opened'(isOpen) {
            if (isOpen) {
                this.closeForm();
            }
        },
        flashcardFocusUuid(uuid) {
            if (uuid) this.scrollToFocusedFlashcard(uuid);
        },
    },
    mounted() {
        this.$bus.$on('confirmDeleteFlashcard', this.handleConfirmDeleteFlashcard);
    },
    beforeDestroy() {
        this.$bus.$off('confirmDeleteFlashcard', this.handleConfirmDeleteFlashcard);
    },
    methods: {
        ...mapActions(['startStudyMode', 'endStudyMode']),
        ...mapMutations(['setFlashcardFocusUuid', 'openConfirmationModal', 'openFlashcardForm', 'closeFlashcardForm']),
        /**
         * Updates the checked colors from the color filter component.
         * @param {Array} colors - Selected color codes
         */
        updateCheckedColors(colors) {
            this.checkedColors = [...colors];
        },
        openCreateForm() {
            this.openFlashcardForm();
        },
        /**
         * Opens the form in edit mode for the given card.
         * @param {Object} card - The flashcard to edit
         */
        openEditForm(card) {
            this.openFlashcardForm({ card });
        },
        closeForm() {
            this.closeFlashcardForm();
        },
        /**
         * Opens the confirmation modal before deleting a flashcard.
         * @param {Object} card - The flashcard instance to delete
         */
        handleDelete(card) {
            this.openConfirmationModal({
                title: this.localizations[this.lang].flashcard.confirmDelete.title,
                message: this.localizations[this.lang].flashcard.confirmDelete.message,
                confirmLabel: this.localizations[this.lang].flashcard.delete,
                cancelLabel: this.localizations[this.lang].flashcard.cancel,
                onConfirmAction: 'confirmDeleteFlashcard',
                context: { card },
            });
        },
        /**
         * Deletes the flashcard through the flashcard model after the user confirms.
         * @param {Object} context - The confirmation context emitted by the modal
         * @param {Object} context.card - The flashcard instance to delete
         */
        handleConfirmDeleteFlashcard({ card }) {
            card.destroy().then(removed => {
                if (removed) this.$bus.$emit('flashcardMarkerRemoved', card.uuid);
            });
        },
        /**
         * Scrolls the panel list to the flashcard with the given uuid and clears the focus flag.
         * Ensures the card's color filter is active first so it isn't hidden, dismisses the study
         * session and create/edit form (which would otherwise replace the list), then waits a frame
         * for the (possibly just-opened) panel to render before scrolling it into view.
         * @param {string} uuid - The target flashcard uuid
         */
        scrollToFocusedFlashcard(uuid) {
            const focusedCard = this.flashcards.find(flashcard => flashcard.uuid === uuid);
            if (focusedCard?.colorSlug && !this.checkedColors.includes(focusedCard.colorSlug)) {
                this.checkedColors = [...this.checkedColors, focusedCard.colorSlug];
            }
            if (this.flashcardStudyMode.active) this.endStudyMode();
            if (this.isFormVisible) this.closeForm();
            this.$nextTick(() => {
                requestAnimationFrame(() => {
                    const cardElement = this.$el.querySelector(`[data-flashcard-uuid="${uuid}"]`);
                    if (cardElement) cardElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    this.setFlashcardFocusUuid(null);
                });
            });
        },
    },
};
</script>
